import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiError = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  return String(detail);
};

const darkInput =
  "w-full rounded-lg border border-white/10 bg-obsidian px-4 py-3 text-sm text-white transition-colors placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-obsidian px-6">
      <SEO title="Owner Login | VCK Business Lab" description="Private owner access." path="/admin/login" noindex />
      <div className="pointer-events-none absolute left-1/2 top-0 h-[360px] w-[600px] -translate-x-1/2 rounded-full bg-gold/10 blur-[120px]" />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-obsidian-card p-10">
        <img src="/assets/vck-logo-dark.png" alt="VCK Business Lab" className="w-64 h-auto" />
        <p className="overline mt-10 text-gold-muted">Owner Access</p>
        <h1 className="mt-3 font-serif text-3xl text-white">Sign in to your dashboard.</h1>
        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-300">Email</label>
            <input
              data-testid="admin-login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={darkInput}
              placeholder="you@vckbusinesslab.com"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-300">Password</label>
            <input
              data-testid="admin-login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={darkInput}
              placeholder="Your password"
            />
          </div>
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="btn-gold w-full disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <Link
          to="/"
          data-testid="admin-login-back-link"
          className="mt-6 block text-center text-xs text-slate-500 transition-colors hover:text-gold"
        >
          Back to website
        </Link>
      </div>
    </div>
  );
}
